<template>
  <div :id="'comment-' + id" class="surface-post-comment">
    <div class="avatar">
      <avatar-image v-if="!author.registered" :user="author" :width="16" :height="16" />
      <a v-if="author.registered" target="_blank" :href="author.profile_url">
        <avatar-image :user="author" :width="16" :height="16" />
      </a>
    </div>
    <div class="comment-main">
      <div class="comment-author small-type">
        <span v-if="!author.registered" class="comment-author-name">{{ authorName(author) }}</span>
        <a v-if="author.registered" :href="author.profile_url" target="_blank" class="comment-author-name">{{
          authorName(author)
        }}</a>
        <relative-time
          class="caption-type comment-author-time"
          :datetime="comment.created_at || new Date().toISOString()"
          dateformat="short"
        />
      </div>
      <div v-if="!isBeingEdited" dir="auto" class="comment-body" v-html="hyperlinkedBody" />
      <div v-if="isBeingEdited">
        <div class="textarea-wrapper">
          <multiline-input
            ref="editCommentInput"
            dir="auto"
            class="add-comment-input"
            :value="body"
            autofocus
            @enter="submitEdit"
          />
        </div>
        <div class="button-list">
          <button class="button small-button text-button" @click="$emit('stop-editing')">{{ __('Cancel') }}</button>
          <button type="submit" class="button small-button text-button" @click="submitEdit">{{ __('Update') }}</button>
        </div>
      </div>
    </div>
    <div v-if="showActions" class="comment-actions">
      <button class="icon-button" :title="__('Comment actions')" @click="toggleDropdown">
        <base-icon size="small" name="more_vert" />
      </button>
    </div>
  </div>
</template>

<script>
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import OutsideEventsMixin from 'vuemixins/outside_events_mixin'
import BaseIcon from 'vuecomponents/base_icon'
import MultilineInput from 'vuecomponents/multiline_input'
import RelativeTime from 'library/v4/components/relative-time'
import AvatarImage from 'vuecomponents/avatar_image'
import { mapState, mapGetters, mapActions } from 'vuex'
import { $ } from 'bits/jquery'

export default {
  components: { BaseIcon, AvatarImage, MultilineInput, RelativeTime },
  mixins: [OutsideEventsMixin],
  props: ['id', 'comment', 'isBeingEdited'],
  computed: {
    ...mapState(['user']),
    ...mapGetters(['canIModerate']),
    ...mapGetters('userContributor', ['getUserById']),
    ...mapGetters('browser', ['isPhone']),
    author() {
      return this.getUserById(this.comment.user_id) || this.user
    },
    isRegistered() {
      return this.author.registered || !!this.author.registered_at
    },
    body() {
      return this.comment.body
    },
    hyperlinkedBody() {
      return this.comment.html_body || this.body
    },
    showActions() {
      return (this.user.id === this.comment.user_id || this.canIModerate) && !this.isBeingEdited
    },
  },
  mounted() {
    this.fetchUser(this.comment.user_id)
  },
  updated() {
    if (this.isBeingEdited && !this.isPhone) {
      this.$refs.editCommentInput.focus()
    }
  },
  methods: {
    ...mapActions('userContributor', ['fetchUser']),
    authorName(author) {
      return this.isRegistered ? this.author.name || this.author.username : this.__('Anonymous')
    },
    toggleDropdown() {
      this.$emit('show-comment-action-menu')
    },
    onEscape() {
      if (this.isBeingEdited) {
        this.$emit('stop-editing')
      }
    },
    onOutsideClick() {
      if (this.isBeingEdited) {
        this.$emit('stop-editing')
      }
    },
    submitEdit() {
      const newBody = this.$refs.editCommentInput.$el.value
      if (newBody || newBody.trim() === '') {
        this.$emit('edit', {
          id: this.id,
          body: newBody,
        })
      }
    },
  },
}
</script>

<style lang="scss">
@import '~styles/3/modules/all';
.surface-post-comment {
  position: relative;
  padding: 6px 12px;
  display: flex;
  align-items: flex-start;
  font-size: 12px;
  line-height: 17px;
  min-height: 40px;

  .comment-author {
    .comment-author-name {
      font-weight: 500;
    }

    relative-time {
      margin-left: 0.5em;
      font-size: 11px;
    }
  }

  .comment-main {
    flex-grow: 1;
  }

  .comment-body {
    word-break: break-word;
    display: block;
  }

  .comment-actions {
    position: absolute;
    top: 0;
    right: 0;
    button {
      padding-right: 0;
      html[data-layout='shelf'] & {
        padding-right: 8px;
      }
    }
  }

  .button-list {
    margin-top: 8px;
    justify-content: flex-end;
  }

  a.comment-author-name {
    color: inherit;

    &:hover {
      text-decoration: underline;
      color: inherit;
    }
    &:visited {
      color: inherit;
    }
  }

  .comment-dropdown-container {
    position: relative;
  }

  .dropdown {
    font-size: $body-font-size;
  }
}

.mousable .surface-post-comment {
  .comment-actions {
    display: none;
  }

  &:hover .comment-actions {
    display: block;
  }
}

.textarea-wrapper {
  flex-grow: 1;

  .add-comment-input {
    font-size: 12px;
    line-height: 17px;
    margin-bottom: 0;
  }
}

.avatar {
  img {
    border-radius: 16px;
    margin-right: 0.5em;
    display: block;
  }
}

[data-view-mode='edit'] {
  .add-comment,
  .comment-actions {
    visibility: hidden;
  }
}

.read-only .add-comment {
  display: none;
}
</style>
